import axios, {AxiosResponse} from 'axios';
import { useEffect, useState } from 'react';


export interface Result {
    startnr: number;
    klass: string;
    fornamn: string;
    efternamn: string;
    fodd: number;
    klubbLag: string;
    loptidSrc: string | null;
    loptid: number | null;
    placering: number;
}
    //klassListOrdning: number; // Order in which to list classes (lower <==> earlier)

export function useGetResults() : [Result[], boolean, React.Dispatch<React.SetStateAction<Result[]>>] {
  const url = "/api/resultat";
  return useGetArrayRequest<Result>(url, true);
}

export async function getResults() {
  const url = "https://ofistadslopp2.azurewebsites.net/api/resultat";
  const response = await getArrayRequest<Result>(url);
  return response;
}

export function useGetArrayRequest<T>(url: string, isValidRequest: boolean, username?: string, password?:string): [T[], boolean, React.Dispatch<React.SetStateAction<T[]>>] {
    const [responseBody, setResponseBody] = useState<T[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        const fetchData = async () => {
            const response = await getArrayRequest<T>(url, username, password);
            setResponseBody(response);
            setIsLoading(false);
        }
        if (isValidRequest) {
          fetchData().catch(
            (e) => {
                console.error(e);
                console.log("FAIL444 " + url);
                setIsLoading(false);
            });
        }
        else {
            setIsLoading(false);
        }
    }, [setResponseBody, setIsLoading, isValidRequest]);

    return [responseBody, isLoading, setResponseBody];
}

async function getArrayRequest<T>(url: string, username?: string, password?:string): Promise<T[]> {
    let header = {};
    if (username && password) {
        header = {Authorization: `Basic ${Buffer.from(`${username}:${password}`).toString("base64")}`}
    }
    const response = await getRequest<T[]>(url, header);
    return response.data;
}


export interface Event {
    id: number;
    itemIds: number[];

    name: string;
    place: string | undefined;
    startTime: number;
    endTime: number;
    description: string;
    maxHeadCount: string | undefined;
    minHeadCount: string | undefined;

    maxAge: number | undefined;
    minAge: number | undefined;
}


export interface Gift {
    id: number,
    giftType: string,
    selectOptionPrompt: string,
    overallGiftSaldo: number | null,
    options: [{id: number, name: string, saldo: number | null, primary: boolean}]
}

export interface Item {
    id: number;

    name: string;
    description: string;
    gift: Gift | undefined,

    minAge: number | undefined;
    minAgeBirthYear: number | undefined;
    maxAge: number | undefined;
    maxAgeBirthYear: number | undefined;

	signupCostInSek : number;
	signupOpenTime : string;
	signupClosedTime : string;
	groupSize : number | undefined;
	maxNumberOfParticipants: number | undefined;
	minNumberOfParticipants: number | undefined;

	resultUnit: string;
	nextStartnumber: number;
}
	
export interface GetSignupsResponse {
    participantId: number,
    eventName: string,
    itemName: string,
    signupOpenTime: string;
    signupClosedTime: string;
    firstName: string,
    lastName: string,
    email: string,
    birthYear: number,
    sportsTeam: string,
    teamName: string,
    latestYearAccepted: number | undefined,
    earliestYearAccepted: number | undefined,
}


export interface SignupResponse {
    abortId: string,
    swishResponseToken: string,
    totalPrice: number,
    nonUtilizedDiscounts: UnutilizedDiscount[]
}

export interface UnutilizedDiscount {
    discount: string,
    reason: string,
}

export interface SignupRequest {
  eventId: number;

  mainEmailAddress: string;
  paymentMethod: string; // SWISH or INVOICE
  swishNumber: string | undefined;

  participants: Participant[];
}

export interface Participant {
  firstName: string;
  lastName: string;
  birthYear: number;
  email: string | undefined;
  sportsTeam: string; // For some reason this may not be undefined
  giftOptionId: number | undefined;
  signups: Signup[];
}


export interface Signup {
    groupName: string | undefined;
    itemId: number;
    discountKeyPhrase: string | undefined;
}

export interface Discount {
    keyPhrase: string;
    usedAmount: number;
    usageLimit: number;
    deadline: string;
    percentageDiscount: number;
}

export interface  InvoiceDetails {
	invoicePayerName: string;
    organizationNumber: string;
	invoiceAddress: string;
	invoiceEmail: string;
	invoicePhone: string;
}


export interface ParticipantUpdate {
    firstName : string;
    lastName: string;
    birthYear: number;
    email: string;
    sportsTeam: string;
}

export async function checkGroupNameFree(eventId: number, name: string | null) {
    const url = `/api/signups/events/${eventId}/verify-group-name/${name}`;
    return (await getRequest<boolean>(url)).data;
}


export async function supplyParticipantUpdate(contractId: string, participantId: number, body: ParticipantUpdate) {
    const url = `/api/signups/${contractId}/participant/${participantId}`
    putRequest<ParticipantUpdate, void>(url, body);
}

export async function supplyInvoiceInfo(contractId: string, body: InvoiceDetails) {
    const url = "/api/signups/invoiceDetails/" + contractId;
    return putRequest<InvoiceDetails, void>(url, body);
}



export async function getVerifyDiscount(eventId: number, discountKey: string) {
    const url = "/api/signups/events/" + eventId + "/verify-discount/?discountKey=" + discountKey;
    return (await getRequest<Discount|undefined>(url)).data;
}

export async function getVerifyGroup(name: string) {
    const url = "/api/signups/events/{eventId}/verify-group-name/?name=" + name;
    return (await getRequest<boolean>(url)).data;
}

export function useGetRequest<T>(url: string, isValidRequest: boolean): [T[], boolean, React.Dispatch<React.SetStateAction<T[] | null>>] {
    const [responseBody, setResponseBody] = useState<T[]|null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        const fetchData = async () => {
            const response = await getRequest<T[]>(url);
            setResponseBody(response.data);
            setIsLoading(false);
        }
        if (isValidRequest) {
          fetchData().catch(
            (e) => {
                console.error(e);
                setIsLoading(false);
            });
        }
        else {
            setIsLoading(false);
        }
    }, [setResponseBody, setIsLoading, isValidRequest]);

    return [responseBody ?? [], isLoading, setResponseBody];
}

export function useGetSignupsRequest(contractId: string | null): [GetSignupsResponse[], boolean, React.Dispatch<React.SetStateAction<GetSignupsResponse[] | null>>] {
    const url = `/api/signups/${contractId}`
    return useGetRequest(url, contractId != null);
}

export function useGetItemsRequest(eventId?: number): [Item[], boolean, any] {
    const url = "/api/events/" + eventId + "/items";
    return useGetRequest(url, eventId != null);
}

export async function postSignupRequest(body: SignupRequest) {
    const url = "/api/signups";
    const response = await postRequest<SignupRequest, SignupResponse>(url, body);
    return response;
}

async function getRequest<T>(url: string, headers?: any) {
    const response = await axios.get<T>(url, 
        {headers: {
            "Content-Type":"application/json",
            ...headers
        },
    });
    return response;
}

async function putRequest<TRequest,TResponse>(url: string, body: TRequest, headers?: any) {
    const response = await axios.put<TResponse, AxiosResponse<TResponse>, TRequest>(url, body,
        {headers: {
            "Content-Type":"application/json",
            ...headers
        },
    });
    return response;
}

async function postRequest<TRequest,TResponse>(url: string, body: TRequest, headers?: any) {
    const response = await axios.post<TResponse|any, AxiosResponse<TResponse>, TRequest>(url, body,
        {headers: {
            "Content-Type":"application/json",
            ...headers
        },
    });
    return response;
}
